import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { KeyRound, SlidersHorizontal, UserCog } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardFooter, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { authApi } from '@/features/auth/api'
import { changePasswordSchema, type ChangePasswordForm } from '@/features/auth/schemas'
import { useAuth } from '@/features/auth/useAuth'
import { LanguagePicker, ThemePicker } from '@/features/preferences/components/PreferenceControls'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDateTime } from '@/utils/format'

const profileSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name.').max(150),
  phone: z
    .string()
    .trim()
    .max(30)
    .transform((v) => (v === '' ? null : v))
    .nullable(),
})

export function AccountPage() {
  const { t } = useTranslation('account')
  useDocumentTitle('Account & preferences')
  const { user, setUser, logout } = useAuth()
  const profile = useForm<z.input<typeof profileSchema>, unknown, z.output<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: user?.name ?? '', phone: user?.phone ?? '' },
  })
  const password = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })
  const saveProfile = useMutation({ mutationFn: authApi.updateProfile })
  const changePassword = useMutation({ mutationFn: authApi.changePassword })

  if (!user) return null

  return (
    <>
      <PageHeader
        title={t('Account & preferences')}
        description={t('Your personal details, preferences and sign-in credentials.')}
        breadcrumbs={[{ label: t('Account & preferences') }]}
      />
      <div className="grid max-w-5xl gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t('Profile')}
            icon={<UserCog />}
            description={t('Signed in as {{email}}', { email: user.email })}
            actions={<Badge tone="brand">{t(user.roleLabel)}</Badge>}
          />
          <form
            noValidate
            onSubmit={profile.handleSubmit(async (v) => {
              try {
                setUser(await saveProfile.mutateAsync(v))
                profile.reset({ name: v.name, phone: v.phone ?? '' })
                toast.success(t('Profile updated'))
              } catch (e) {
                applyServerErrors(e, profile.setError, ['name', 'phone'])
              }
            })}
          >
            <CardBody className="space-y-4">
              <FormField label={t('Full name')} error={profile.formState.errors.name?.message} required>
                <Input {...profile.register('name')} />
              </FormField>
              <FormField label={t('Phone')} error={profile.formState.errors.phone?.message}>
                <Input type="tel" {...profile.register('phone')} />
              </FormField>
              <p className="text-xs text-muted">
                {t('Last sign-in: {{date}}', { date: formatDateTime(user.lastLoginAt) })}
              </p>
            </CardBody>
            <CardFooter>
              <Button
                type="submit"
                loading={profile.formState.isSubmitting}
                disabled={!profile.formState.isDirty}
              >
                {t('Save profile')}
              </Button>
            </CardFooter>
          </form>
        </Card>

        <Card className="order-first lg:col-span-2">
          <CardHeader
            title={t('Preferences')}
            icon={<SlidersHorizontal />}
            description={t('Saved to your account and applied on every device.')}
          />
          <CardBody className="grid gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-[0.8125rem] font-medium text-ink-soft">{t('Language')}</span>
              <LanguagePicker />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[0.8125rem] font-medium text-ink-soft">{t('Theme')}</span>
              <ThemePicker />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('Password')}
            icon={<KeyRound />}
            description={t('Changing it signs you out on every device.')}
          />
          <form
            noValidate
            onSubmit={password.handleSubmit(async (v) => {
              try {
                await changePassword.mutateAsync({
                  currentPassword: v.currentPassword,
                  newPassword: v.newPassword,
                })
                toast.success(t('Password changed'), t('Please sign in again.'))
                await logout()
              } catch (e) {
                applyServerErrors(e, password.setError, ['currentPassword', 'newPassword'])
              }
            })}
          >
            <CardBody className="space-y-4">
              <Alert tone="info">{t('Use at least 8 characters. Avoid passwords you use elsewhere.')}</Alert>
              <FormField
                label={t('Current password')}
                error={password.formState.errors.currentPassword?.message}
                required
              >
                <Input
                  type="password"
                  autoComplete="current-password"
                  {...password.register('currentPassword')}
                />
              </FormField>
              <FormField
                label={t('New password')}
                error={password.formState.errors.newPassword?.message}
                required
              >
                <Input type="password" autoComplete="new-password" {...password.register('newPassword')} />
              </FormField>
              <FormField
                label={t('Confirm new password')}
                error={password.formState.errors.confirmPassword?.message}
                required
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...password.register('confirmPassword')}
                />
              </FormField>
            </CardBody>
            <CardFooter>
              <Button type="submit" loading={password.formState.isSubmitting}>
                {t('Change password')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </>
  )
}
